import "./ContestPreviousWinnersTable.scss"
import { gettext, interpolate } from "@multimediallc/web-utils"
import { useAppContext } from "../../../hooks/appContext"
import { Weights } from "../../../interfaces/sizing"
import { Typography } from "../../common"
import { ContestPreviousWinnerRow } from "../ContestPreviousWinnerRow"
import type { IPreviousWinner } from "../ContestPreviousWinners/ContestPreviousWinners"

const i18n = {
    previousWinnersHeadingText: (day: string): string => {
        return interpolate(
            gettext("%(day)s's Contest Winners"),
            { day: day },
            true,
        )
    },
    today: gettext("Today"),
    yesterday: gettext("Yesterday"),
}

interface ContestPreviousWinnersTableProps {
    /** The selected date for the previous winners, in YYYY-M-D format */
    selectedDate: string
    /** The data for previous winners */
    previousWinners: IPreviousWinner[]
}

export function ContestPreviousWinnersTable({
    selectedDate,
    previousWinners,
}: ContestPreviousWinnersTableProps) {
    /** Create heading text for previous winners, In a way, this mimics django's naturalday tag
     * If it's today, then heading would be "Today's ..."
     * If it's 1 day ago, then heading would be "Yesterday's ..."
     * Other than that, then heading would be "<Month_name> <Date>, <Year>'s..."
     */

    const languageCode = useAppContext().context.language_code
    const createHeadingTextFromDate = (): string => {
        // Default selected date to today if there's nothing
        // eslint-disable-next-line @multimediallc/no-underscore-prefix
        let _selectedDate = new Date()
        if (selectedDate !== "") {
            const [year, month, day] = selectedDate
                .split("-")
                .map((x) => parseInt(x))
            _selectedDate = new Date(year, month - 1, day)
        }
        const today = new Date()
        const isToday = _selectedDate.toDateString() === today.toDateString()
        if (isToday) {
            return i18n.previousWinnersHeadingText(i18n.today)
        }
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        const isYesterday =
            _selectedDate.toDateString() === yesterday.toDateString()
        if (isYesterday) {
            return i18n.previousWinnersHeadingText(i18n.yesterday)
        }
        return i18n.previousWinnersHeadingText(
            new Date(_selectedDate).toLocaleDateString(languageCode, {
                year: "numeric",
                month: "long",
                day: "numeric",
            }),
        )
    }

    return (
        <div className="ContestPreviousWinnersTable">
            <Typography
                weight={Weights.medium}
                component="h2"
                className="heading"
            >
                {createHeadingTextFromDate()}
            </Typography>
            {previousWinners.map((winner: IPreviousWinner, index) => {
                return (
                    <ContestPreviousWinnerRow
                        {...winner}
                        key={
                            winner.username !== undefined
                                ? `${winner.username}-${index}`
                                : index
                        }
                    />
                )
            })}
        </div>
    )
}
